// #74 SplitButton: a ghost Button (the verb: *Hold*, *Pass*) beside a chevron IconButton that opens a Menu (#73) of
// the reason codes the Admin configures (aria-haspopup="menu", aria-expanded, aria-controls). The chevron opens the
// list on Enter, Space or ArrowDown (first reason) and ArrowUp (last); choosing a reason, Escape or Tab closes it and
// focus returns to the chevron; a press outside closes it. Blocked (DC-24): both halves are dashed, aria-disabled and
// described by the inline reason; the list never opens. Busy: the verb keeps its words under aria-busy and both
// halves ignore activation. `preview`: a gallery section's static rendering of the open list.

import { forwardRef, useRef, useState, type KeyboardEvent, type MutableRefObject } from "react";
import { Button, IconButton, type ControlProps } from "../core/Button";
import { Menu, type MenuItem } from "../core/Menu";
import { cx } from "../lib/cx";
import { useSvgId } from "../lib/useSvgId";
import { G1_LABELS } from "./labels";
import type { Hs } from "../lib/hs";

export interface SplitButtonProps extends Omit<ControlProps, "hs"> {
  /** The verb (G §17), from a labels module. */
  label: string;
  /** The main half's action (the verb with the default reason, or opening its form). */
  onClick?: (() => void) | undefined;
  /** The reason codes (the Admin's configuration). */
  reasons: readonly MenuItem[];
  /** The chevron's name (aria-label and tooltip); default *Reason*. */
  menuLabel?: string | undefined;
  sm?: boolean | undefined;
  busy?: boolean | undefined;
  open?: boolean | undefined;
  defaultOpen?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  preview?: boolean | undefined;
  /** Hotspots: the main half and the chevron. */
  hs?: { main?: Hs | undefined; menu?: Hs | undefined } | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export const SplitButton = forwardRef<HTMLButtonElement, SplitButtonProps>(function SplitButton(
  {
    label, onClick, reasons, menuLabel = G1_LABELS.reason, sm, busy, open, defaultOpen = false, onOpenChange, preview, hs, id,
    can, blocked, mode, className,
  },
  forwarded,
) {
  const generated = useSvgId();
  const listId = id ?? `${generated}-menu`;
  const chevronId = `${generated}-chev`;
  const [own, setOwn] = useState(defaultOpen);
  const [focusOn, setFocusOn] = useState<"first" | "last" | undefined>(undefined);
  const chevron = useRef<HTMLButtonElement | null>(null);
  const isOpen = (open ?? own) && !blocked && !busy;
  const change = (next: boolean, focus?: "first" | "last") => {
    setFocusOn(next ? focus : undefined);
    if (open === undefined) setOwn(next);
    onOpenChange?.(next);
  };
  const setMain = (element: HTMLButtonElement | null) => {
    if (typeof forwarded === "function") forwarded(element);
    else if (forwarded) (forwarded as MutableRefObject<HTMLButtonElement | null>).current = element;
  };
  if (can === false || mode === "view") return null;
  const inert = Boolean(blocked) || Boolean(busy);
  return (
    <span className={cx("mc-splitbtn", sm && "mc-splitbtn-sm", className)}>
      <Button ref={setMain} tone="ghost" sm={sm} busy={busy} blocked={blocked} hs={hs?.main} className="mc-splitbtn-main" onClick={() => onClick?.()}>
        {label}
      </Button>
      <IconButton
        ref={chevron}
        id={chevronId}
        icon="down"
        label={menuLabel}
        sm={sm}
        blocked={blocked}
        className="mc-splitbtn-chev"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={listId}
        hs={hs?.menu}
        onClick={() => { if (!inert) change(!isOpen, "first"); }}
        onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
          if (inert) return;
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            change(true, event.key === "ArrowDown" ? "first" : "last");
          } else if (event.key === "Escape" && isOpen) {
            event.preventDefault();
            change(false);
          }
        }}
      />
      <Menu
        items={reasons}
        open={isOpen}
        onClose={() => change(false)}
        id={listId}
        anchor={chevron}
        labelledBy={chevronId}
        autoFocus={focusOn}
        preview={preview}
      />
    </span>
  );
});
